import React from "react";
import { CalendarDays, Download, FileText, Mail, PenLine } from "lucide-react";
import PropertyInlineSummary from "../components/PropertyInlineSummary";
import { IPendingSignatureItem } from "../types/ownerDashboard.types";
import { RentedPropertyCatalog } from "../utils/buildRentedPropertyCatalog";
import {
  formatPendingSignatureDate,
  getPendingSignatureDaysAgo,
} from "../utils/formatPendingSignatureDate";
import { buildPropertyListingView } from "../utils/rentedPropertyDisplay";

interface PendingSignatureCardProps {
  item: IPendingSignatureItem;
  isDownloading?: boolean;
  isNotifying?: boolean;
  rentedPropertyCatalog?: RentedPropertyCatalog;
  onSign: (item: IPendingSignatureItem) => void;
  onDownload: (item: IPendingSignatureItem) => void;
}

function getStatusMeta(item: IPendingSignatureItem) {
  if (item.document_status === "signed") {
    return {
      label: "Assinado",
      className: "border-emerald-500/40 bg-emerald-500/15 text-emerald-400",
    };
  }

  if (item.document_status === "canceled") {
    return {
      label: "Cancelado",
      className: "border-red-500/40 bg-red-500/15 text-red-400",
    };
  }

  return {
    label: "Pendente",
    className: "border-amber-500/40 bg-amber-500/15 text-amber-400",
  };
}

export default function PendingSignatureCard({
  item,
  isDownloading = false,
  isNotifying = false,
  rentedPropertyCatalog,
  onSign,
  onDownload,
}: PendingSignatureCardProps) {
  const daysAgo = getPendingSignatureDaysAgo(item.created_at);
  const formattedDate = formatPendingSignatureDate(item.created_at);
  const status = getStatusMeta(item);
  const isEmailSign = item.sign_mode === "email";
  const canShowSignAction =
    item.can_sign || (isEmailSign && item.document_status === "pending");
  const signLabel = isEmailSign
    ? isNotifying
      ? "Enviando..."
      : item.can_sign
        ? "Assinar por e-mail"
        : "Reenviar e-mail"
    : "Assinar";
  const detail = rentedPropertyCatalog?.findByPropertyId(item.property_id);
  const listing = buildPropertyListingView(detail, {
    address: item.subtitle,
  });
  const showPropertySummary = Boolean(item.subtitle);

  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl border border-white/[0.08] bg-[#1c2128] p-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full border px-2.5 py-0.5 text-[0.65rem] font-semibold ${status.className}`}
          >
            {status.label}
          </span>
          <div className="flex items-center gap-2 text-xs text-[#8b949e]">
            <CalendarDays className="h-3.5 w-3.5 shrink-0 text-[#6ea8ff]" />
            <span>
              {daysAgo != null ? `Enviado há ${daysAgo} dias` : "Enviado"}
              {formattedDate ? ` • ${formattedDate}` : ""}
            </span>
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-start gap-2">
            <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#6ea8ff]" />
            <p className="text-sm font-semibold text-[#f0f6fc]">{item.title}</p>
          </div>
          {showPropertySummary && (
            <div className="pl-5">
              <PropertyInlineSummary listing={listing} className="line-clamp-2" />
            </div>
          )}
        </div>

        {item.tenant_name && (
          <p className="truncate text-xs text-[#c9d1d9]">
            Inquilino: <span className="font-semibold text-[#f0f6fc]">{item.tenant_name}</span>
          </p>
        )}

        {isEmailSign && item.document_status === "pending" && (
          <p className="text-xs text-[#8b949e]">
            {item.can_sign
              ? "A assinatura será enviada por e-mail para os signatários."
              : "Link já solicitado. Você pode reenviar o e-mail se necessário."}
          </p>
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-2 sm:items-end">
        {canShowSignAction && (
          <button
            type="button"
            onClick={() => onSign(item)}
            disabled={isNotifying}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#6ea8ff] px-4 py-2 text-xs font-bold text-[#0d1117] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isEmailSign ? <Mail className="h-4 w-4" /> : <PenLine className="h-4 w-4" />}
            {signLabel}
          </button>
        )}

        <button
          type="button"
          onClick={() => onDownload(item)}
          disabled={!item.can_download || isDownloading}
          title={
            item.can_download
              ? "Baixar contrato"
              : "Disponível após a finalização de todas as assinaturas"
          }
          className="inline-flex items-center justify-center gap-2 rounded-full border border-[#6ea8ff]/35 px-4 py-2 text-xs font-semibold text-[#6ea8ff] transition-colors hover:border-[#6ea8ff]/55 hover:bg-[#6ea8ff]/10 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Download className="h-4 w-4" />
          {isDownloading ? "Baixando..." : "Baixar contrato"}
        </button>
      </div>
    </div>
  );
}
